/**
 * /settings/tags — list + create + delete tags.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface TagRow {
  id: string;
  name: string;
  color: string | null;
  contactCount: number;
  createdAt: string;
}

export const Route = createFileRoute("/_app/settings/tags")({
  component: TagsPage,
});

function TagsPage() {
  const [tags, setTags] = useState<TagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ tags: TagRow[] }>("/tags");
      setTags(r.data.tags);
    } catch {
      setError("Failed to load tags");
    } finally {
      setLoading(false);
    }
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/tags", {
        name: name.trim(),
        color: color || null,
      });
      setName("");
      setColor("");
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to create tag");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this tag? It will be removed from all contacts.")) return;
    try {
      await api.delete(`/tags/${id}`);
      setTags((cur) => cur.filter((t) => t.id !== id));
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Tags</h1>
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          border: "var(--border-hair)",
          borderRadius: 12,
          background: "var(--bg-surface)",
          fontSize: 11,
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 16,
        }}
      >
        Shared across all Instagram accounts
      </span>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Workspace-scoped contact labels. Names are case-insensitive.
      </p>

      <form
        onSubmit={create}
        className="brut-card"
        style={{ marginBottom: 24, display: "grid", gap: 10 }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>New tag</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 140px auto" }}>
          <input
            placeholder="vip"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="brut-input"
            maxLength={64}
            required
          />
          <input
            placeholder="#ffaa00 (optional)"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="brut-input"
          />
          <button type="submit" disabled={!name.trim() || busy} className="brut-button">
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </form>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : tags.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No tags yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Tags label contacts so segments + flows can target them. Create
            one above ↑, then apply it from a contact's profile or via a
            flow's <code>set_tag</code> node.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {tags.map((t) => (
            <li
              key={t.id}
              className="brut-card"
              style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                {t.color ? (
                  <span
                    style={{
                      display: "inline-block",
                      width: 16,
                      height: 16,
                      background: t.color,
                      border: "2px solid var(--ink)",
                    }}
                  />
                ) : null}
                <span style={{ fontWeight: 700, fontSize: 16 }}>{t.name}</span>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>
                  · {t.contactCount} {t.contactCount === 1 ? "contact" : "contacts"}
                </span>
              </div>
              <button
                onClick={() => void remove(t.id)}
                className="brut-button is-danger"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
